import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Calculator, Info, ExternalLink, Check, GitCompare } from "lucide-react";
import SEO from "@/components/SEO";
import CtaBanner from "@/components/CtaBanner";
import { MARKETPLACES_RATES, CATEGORIES, calculate } from "@/data/marketplace-rates";
import { COMPANY } from "@/data/content";

const inr = (n) => `₹${Math.max(0, Math.round(n)).toLocaleString("en-IN")}`;

function Field({ label, children, hint }) {
  return (
    <label className="block">
      <div className="text-xs font-bold uppercase tracking-widest text-black/70">{label}</div>
      {children}
      {hint && <div className="mt-1 text-xs text-black/50">{hint}</div>}
    </label>
  );
}

export default function MarketplaceCalculatorHub() {
  const [mode, setMode] = useState("single"); // single | compare
  const [selectedId, setSelectedId] = useState("amazon");
  const [inputs, setInputs] = useState({
    sellingPrice: 899,
    productCost: 250,
    gstPct: 18,
    category: "Beauty & Personal Care",
    weightKg: 0.4,
    fulfilment: "FBA",
    adCostPerUnit: 60,
    returnRatePct: 5,
    packagingCost: 20
  });
  const [compareIds, setCompareIds] = useState(["amazon", "flipkart", "meesho"]);

  const selected = MARKETPLACES_RATES.find((m) => m.id === selectedId);
  const singleResult = useMemo(() => selected ? calculate(selected, inputs) : null, [selected, inputs]);
  const compareResults = useMemo(() => compareIds.map(id => {
    const mp = MARKETPLACES_RATES.find((m) => m.id === id);
    return { mp, res: mp ? calculate(mp, inputs) : null };
  }).filter(x => x.mp), [compareIds, inputs]);

  const num = COMPANY.whatsapp.replace(/\D/g, "");
  const waMsg = encodeURIComponent(`Hi Orina, I've used the marketplace calculator for ${selected?.name} and want help improving my numbers.`);

  const schema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Indian Marketplace Seller Fee & Profit Calculators",
    "url": "https://orinaenterprise.com/tools/marketplace-calculators/",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Any",
    "isAccessibleForFree": true,
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "INR" },
    "provider": { "@id": "https://orinaenterprise.com/#organization" }
  };

  return (
    <>
      <SEO
        title="Indian Marketplace Seller Fee & Profit Calculators"
        description="Free calculators for Amazon, Flipkart, Meesho, JioMart, Myntra, AJIO, Nykaa, Tata CLiQ, Blinkit, Zepto, Instamart and Swiggy Mall. Model fees, settlement, profit, margin and ROI."
        keywords="Amazon fee calculator India, Flipkart profit calculator, Meesho seller calculator, marketplace profit calculator India"
        path="/tools/marketplace-calculators"
        schema={schema}
        breadcrumbs={[{ name: "Home", path: "/" }, { name: "Tools", path: "/tools/marketplace-calculators" }, { name: "Marketplace Calculators", path: "/tools/marketplace-calculators" }]}
      />

      <section className="border-b-2 border-black bg-[#FFD700] py-12 md:py-20">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="tape-dark">Tools</div>
          <h1 className="display mt-6 max-w-4xl text-4xl font-black leading-[1.02] tracking-tight md:text-5xl lg:text-6xl">
            Indian Marketplace Seller Fee & Profit Calculators
          </h1>
          <p className="mt-4 max-w-3xl text-base text-black/85 md:text-lg">
            Pick a marketplace, enter your numbers, get an instant fee breakdown, settlement and net profit. Compare marketplaces side-by-side to plan channel mix.
          </p>
          <p className="mt-4 flex items-start gap-2 text-xs text-black/70 md:text-sm">
            <Info size={16} className="mt-0.5 shrink-0" />
            <span><strong>Indicative estimate only.</strong> Marketplace fees vary by category, price band, weight, fulfilment, seller tier, promotional programs and account-specific terms. Always confirm settlement using the marketplace's own Fee Preview / Rate Card.</span>
          </p>
        </div>
      </section>

      <section className="bg-white py-10 md:py-16">
        <div className="mx-auto max-w-7xl px-4 md:px-8">
          <div className="flex flex-wrap items-center gap-2">
            <button data-testid="calc-mode-single" onClick={() => setMode("single")} className={`border-2 border-black px-4 py-2 text-sm font-bold uppercase ${mode === "single" ? "bg-black text-[#FFD700]" : "bg-white text-black"}`}>Single Marketplace</button>
            <button data-testid="calc-mode-compare" onClick={() => setMode("compare")} className={`border-2 border-black px-4 py-2 text-sm font-bold uppercase ${mode === "compare" ? "bg-black text-[#FFD700]" : "bg-white text-black"}`}><GitCompare size={14} className="mr-1 inline"/> Compare</button>
          </div>

          {mode === "single" && (
            <div className="mt-8 grid gap-8 lg:grid-cols-12">
              <div className="lg:col-span-5 space-y-4">
                <Field label="Marketplace">
                  <select data-testid="calc-marketplace" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} className="mt-1 w-full border-2 border-black bg-white px-3 py-3 text-base font-bold">
                    {MARKETPLACES_RATES.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </Field>
                <Field label="Category">
                  <select value={inputs.category} onChange={(e) => setInputs({ ...inputs, category: e.target.value })} className="mt-1 w-full border-2 border-black bg-white px-3 py-3 text-base font-bold">
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </Field>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Field label="Selling price (₹)"><input type="number" value={inputs.sellingPrice} onChange={(e) => setInputs({ ...inputs, sellingPrice: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                  <Field label="Product cost (₹)"><input type="number" value={inputs.productCost} onChange={(e) => setInputs({ ...inputs, productCost: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                  <Field label="GST rate (%)"><input type="number" value={inputs.gstPct} onChange={(e) => setInputs({ ...inputs, gstPct: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                  <Field label="Weight (kg)"><input type="number" step="0.1" value={inputs.weightKg} onChange={(e) => setInputs({ ...inputs, weightKg: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                  <Field label="Fulfilment">
                    <select value={inputs.fulfilment} onChange={(e) => setInputs({ ...inputs, fulfilment: e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2">
                      <option>FBA</option>
                      <option>Easy Ship</option>
                      <option>Self Ship</option>
                    </select>
                  </Field>
                  <Field label="Ad cost / unit (₹)"><input type="number" value={inputs.adCostPerUnit} onChange={(e) => setInputs({ ...inputs, adCostPerUnit: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                  <Field label="Return rate (%)"><input type="number" value={inputs.returnRatePct} onChange={(e) => setInputs({ ...inputs, returnRatePct: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                  <Field label="Packaging (₹)"><input type="number" value={inputs.packagingCost} onChange={(e) => setInputs({ ...inputs, packagingCost: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                </div>
                <div className="mt-2 text-xs text-black/60">
                  Rate card for <strong>{selected?.name}</strong> — last updated {selected?.lastUpdated}. <a href={selected?.sourceUrl} target="_blank" rel="noreferrer" className="underline">Official source <ExternalLink size={12} className="inline"/></a>
                </div>
              </div>

              <div className="lg:col-span-7">
                {singleResult && (
                  <div className="border-2 border-black bg-black text-white shadow-brutal-lg">
                    <div className="bg-[#FFD700] p-6 text-black">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-xs font-bold uppercase tracking-widest">Net profit / unit on {selected.name}</div>
                          <div className="display mt-1 text-5xl font-black" data-testid="calc-net-profit">{inr(singleResult.netProfit)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs font-bold uppercase tracking-widest">Margin</div>
                          <div className="display mt-1 text-3xl font-black">{singleResult.marginPct.toFixed(1)}%</div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 space-y-3 text-sm">
                      <Row label={`Marketplace commission (${singleResult.referralPct}%)`} v={-singleResult.referralFee} />
                      <Row label="Closing / fixed fee" v={-singleResult.closingFee} />
                      <Row label="Fulfilment fee" v={-singleResult.fulfilmentFee} />
                      <Row label="Weight fee" v={-singleResult.weightFee} />
                      <Row label="GST on fees (18%)" v={-singleResult.gstOnFees} />
                      <Row label="Estimated return cost / unit" v={-singleResult.returnCostPerUnit} />
                      <Row label="Ad cost / unit" v={-inputs.adCostPerUnit} />
                      <Row label="Packaging" v={-inputs.packagingCost} />
                      <div className="border-t-2 border-white/30 pt-3">
                        <Row label="Settlement (before COGS)" v={singleResult.settlement} />
                        <Row label="Product cost" v={-inputs.productCost} />
                      </div>
                      <div className="border-t-2 border-[#FFD700] pt-3">
                        <div className="flex items-center justify-between text-lg font-black">
                          <span>NET PROFIT / UNIT</span>
                          <span>{inr(singleResult.netProfit)}</span>
                        </div>
                        <div className="mt-1 flex justify-between text-xs text-white/70">
                          <span>ROI on product cost</span><span>{singleResult.roiPct.toFixed(1)}%</span>
                        </div>
                        <div className="flex justify-between text-xs text-white/70">
                          <span>Break-even price</span><span>{inr(singleResult.breakEvenPrice)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link to="/contact/?type=expert" className="btn-brutal">Talk to Orina about these numbers <ArrowUpRight size={16} /></Link>
                  <a href={`https://wa.me/${num}?text=${waMsg}`} target="_blank" rel="noreferrer" className="btn-brutal secondary">WhatsApp us the numbers</a>
                </div>
              </div>
            </div>
          )}

          {mode === "compare" && (
            <div className="mt-8">
              <div className="grid gap-3 md:grid-cols-4">
                {MARKETPLACES_RATES.map((m) => (
                  <label key={m.id} className={`flex cursor-pointer items-center gap-2 border-2 border-black px-3 py-2 text-sm font-bold ${compareIds.includes(m.id) ? "bg-[#FFD700]" : "bg-white"}`}>
                    <input type="checkbox" className="h-4 w-4" checked={compareIds.includes(m.id)} onChange={(e) => setCompareIds(e.target.checked ? [...compareIds, m.id] : compareIds.filter(id => id !== m.id))} />
                    {m.name}
                  </label>
                ))}
              </div>
              <div className="mt-6 grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                <Field label="Selling price (₹)"><input type="number" value={inputs.sellingPrice} onChange={(e) => setInputs({ ...inputs, sellingPrice: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                <Field label="Product cost (₹)"><input type="number" value={inputs.productCost} onChange={(e) => setInputs({ ...inputs, productCost: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                <Field label="Weight (kg)"><input type="number" step="0.1" value={inputs.weightKg} onChange={(e) => setInputs({ ...inputs, weightKg: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
                <Field label="Return rate (%)"><input type="number" value={inputs.returnRatePct} onChange={(e) => setInputs({ ...inputs, returnRatePct: +e.target.value })} className="mt-1 w-full border-2 border-black px-3 py-2" /></Field>
              </div>
              <div className="mt-8 overflow-x-auto">
                <table className="w-full min-w-[680px] border-2 border-black text-sm">
                  <thead className="bg-black text-[#FFD700]">
                    <tr>
                      <th className="p-3 text-left">Marketplace</th>
                      <th className="p-3 text-right">Fees</th>
                      <th className="p-3 text-right">Settlement</th>
                      <th className="p-3 text-right">Net Profit</th>
                      <th className="p-3 text-right">Margin</th>
                      <th className="p-3 text-right">ROI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compareResults.map(({ mp, res }) => (
                      <tr key={mp.id} className="border-t-2 border-black">
                        <td className="p-3 font-bold">{mp.name}</td>
                        <td className="p-3 text-right">{inr(res.totalMarketplaceFees + res.gstOnFees)}</td>
                        <td className="p-3 text-right">{inr(res.settlement)}</td>
                        <td className="p-3 text-right font-black">{inr(res.netProfit)}</td>
                        <td className="p-3 text-right">{res.marginPct.toFixed(1)}%</td>
                        <td className="p-3 text-right">{res.roiPct.toFixed(1)}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-3 text-xs text-black/60">Numbers are indicative only. Actual settlements depend on your marketplace rate card and category-specific fees.</p>
              <div className="mt-6">
                <Link to="/contact/?type=expert" className="btn-brutal">Want help choosing the right marketplace mix? Talk to Orina <ArrowUpRight size={16}/></Link>
              </div>
            </div>
          )}
        </div>
      </section>

      <CtaBanner title="Model your marketplace economics — then run them for real" subtitle="Orina Enterprise runs marketplaces across Amazon, Flipkart, Meesho, JioMart, Myntra, Nykaa, AJIO, Tata CLiQ, Blinkit, Zepto, Instamart and Swiggy Mall." />
    </>
  );
}

function Row({ label, v }) {
  const positive = v >= 0;
  return (
    <div className="flex items-center justify-between">
      <span className="text-white/85">{label}</span>
      <span className={`font-bold ${positive ? "text-[#25D366]" : "text-white"}`}>{positive ? "+" : ""}{inr(v)}</span>
    </div>
  );
}
